import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { BaseAxesChart } from '@components/ui-chart/base-axes-chart';
import { IChartCallbackParams } from '@models';

import defaultsDeep from 'lodash-es/defaultsDeep';

import { BASE_SERIES_OPTIONS } from './ui-bar-chart.constants';
import {
    BarChartData,
    BarChartDataValue,
    isBarChartDataComplexValue,
} from './ui-bar-chart.types';

@Component({
    template: '',
    selector: 'ui-bar-chart',
    styleUrls: ['../base-chart.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiBarChartComponent
    extends BaseAxesChart<never>
    implements OnChanges, OnDestroy {

    @Input()
    public data?: BarChartData;

    @Input()
    public color?: string;

    @Input()
    public tooltipFormatter?: (params: IChartCallbackParams) => string;

    @Input()
    public valueFormatter?: (value: number | Array<any>) => string;

    constructor(
        injector: Injector,
        protected _viewContainerRef: ViewContainerRef,
    ) {
        super(injector);
        this.chartOptions.tooltip.formatter = this._getTooltipText;
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (
            !this.data ||
            (
                !changes.data &&
                !changes.legend &&
                !changes.colors &&
                !changes.color &&
                !changes.xAxisLabels
            )
        ) { return; }

        if (
            this.color != null &&
            this.colors.length
        ) {
            throw new Error(`The bar chart configuration only accepts usage of either 'color' or 'colors' component inputs.`);
        }

        if (
            this.xAxisLabels != null &&
            this.showLegend
        ) {
            // The component only supports simple bar charts and we're limited by how ECharts treats X axis labels and legend items.
            // An X axis label applies to all data items found at the same index in chartOptions.series[all].data[index]
            // A legend item applies to an entire chartOptions.series
            // Depending of which we want to show (X axis labels or legend), the chartOptions.series array will look differently.
            throw new Error(`The bar chart configuration does not support both X Axis Labels and Legend at the same time.`);
        }

        this.chartOptions.series = [];
        this.chartOptions.legend.data = [];
        this.chartOptions.xAxis.data = [];

        if (this.color != null) {
            this.chartOptions.color = [this.color];
        }

        if (this.xAxisLabels) {
            this.chartOptions.xAxis.data = this.xAxisLabels;

            this.chartOptions.series = [defaultsDeep(
                {
                    data: [],
                },
                BASE_SERIES_OPTIONS,
            )];

        }

        this.data.forEach(this._mapData);
    }

    private _getTooltipText = (params: IChartCallbackParams) => {
        if (this.tooltipFormatter) {
            return this.tooltipFormatter(params);
        }

        const currentValue = this.data![params.dataIndex];

        const subtitle = isBarChartDataComplexValue(currentValue) && currentValue.tooltipName ?
            currentValue.tooltipName :
            params.name ?
                params.name :
                params.seriesName;

        const value = this.valueFormatter ?
            this.valueFormatter(params.value) :
            params.value;

        return `
            <div class="chart-tooltip-container">
                <div class="tooltip-title">
                    <span class="tooltip-marker">${params.marker}</span>
                    <strong>${value}</strong>
                </div>
                <div class="tooltip-subtitle">
                    ${subtitle}
                </div>
            </div>`;
    }

    private _mapData = (rawData: BarChartDataValue, index: number) => {
        const value = isBarChartDataComplexValue(rawData) ?
            rawData.value :
            rawData;

        if (this.xAxisLabels) {
            const dataSet: any = {
                value,
            };

            if (this.colors) {
                dataSet.itemStyle = {
                    color: this.colors[index] || null,
                };
            }

            this.chartOptions.series![0].data.push(dataSet);

            return;
        }

        const series = defaultsDeep(
            {
                name: (this.legend || [])[index] || '',
                data: [{ value }],
            },
            BASE_SERIES_OPTIONS,
        );

        if (this.colors) {
            series.itemStyle = {
                color: this.colors[index] || null,
            };
        }

        this.chartOptions.series!.push(series);
        this.chartOptions.legend.data.push({
            name: (this.legend || [])[index] || '',
            icon: 'circle',
        });
    }
}
